import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, X } from "lucide-react";
import { AuthContext } from "../../context/auth";
import InviteCodeModal from "../inviteCodeModal/InviteCodeModal";
import { addGuestToEvent } from "../../helper/addGuestToEvent";
import axios from "axios";
import "./PrivateEventModal.css";

const PrivateEventModal = ({ isOpen, onClose, event }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [showInviteCodeModal, setShowInviteCodeModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  if (!isOpen || !event) return null;

  const isAuthenticated = !!user;

  const handleEnterInviteCode = () => {
    if (isAuthenticated) {
      setShowInviteCodeModal(true);
    } else {
      onClose();
      navigate("/onboarding", {
        state: {
          mode: "signup",
          preSelectedRole: "guest",
          eventId: event._id,
          eventTitle: event.eventTitle,
        },
      });
    }
  };

  const handleInviteCodeProceed = async (code) => {
    if (!code.trim()) {
      setErrorMessage("Please enter an invite code");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const hostLookup = await axios.get(`/invite`, {
        params: { code, role: "guest" },
      });

      if (!hostLookup.data?.success) {
        setErrorMessage(hostLookup.data?.message || "Invalid invite code");
        setIsLoading(false);
        return;
      }

      const hostId = hostLookup.data.event.userId;

      const { data: verifyData } = await axios.get(`/invite/${hostId}`, {
        params: {
          code,
          role: "guest",
          userId: user._id,
        },
      });

      if (!verifyData?.success && !verifyData?.successStatus) {
        setErrorMessage(verifyData?.message || "Invalid invite code");
        setIsLoading(false);
        return;
      }

      const addResponse = await addGuestToEvent(code, hostId, user._id);

      if (addResponse.isEventDatePassed) {
        setErrorMessage("Event date has passed!");
        setIsLoading(false);
        return;
      }

      if (addResponse.isWrongRoleCode) {
        setErrorMessage("This code is not for your role.");
        setIsLoading(false);
        return;
      }

      if (addResponse.isAlreadyInvited) {
        setErrorMessage("You are already invited to this event.");
        setIsLoading(false);
        return;
      }

      setShowInviteCodeModal(false);
      onClose();
      navigate("/invites", { state: { reloadHome: true } });
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Failed to verify invite";
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCloseInviteCodeModal = () => {
    setShowInviteCodeModal(false);
    setErrorMessage("");
  };

  return (
    <>
      <div className="private-event-modal-overlay" onClick={handleOverlayClick}>
        <div className="private-event-modal-container">
          <button className="private-event-modal-close" onClick={onClose}>
            <X size={24} />
          </button>

          <div className="private-event-modal-icon">
            <Lock size={64} />
          </div>

          <h2 className="private-event-modal-title">Private Event</h2>

          <p className="private-event-modal-message">
            This is a private event and can only be accessed with an invite
            code.
          </p>

          {event && (
            <div className="private-event-modal-event-info">
              <h3 className="private-event-modal-event-title">
                {event.eventTitle}
              </h3>
              <p className="private-event-modal-event-type">
                {event.eventType}
              </p>
            </div>
          )}

          <div className="private-event-modal-actions">
            <button
              className="private-event-modal-primary-btn"
              onClick={handleEnterInviteCode}
            >
              Enter Invite Code
            </button>

            <button
              className="private-event-modal-secondary-btn"
              onClick={onClose}
            >
              Back to Explore
            </button>
          </div>

          <p className="private-event-modal-help-text">
            Don't have an invite code? Contact the event host to request access.
          </p>
        </div>
      </div>

      {isAuthenticated && showInviteCodeModal && (
        <InviteCodeModal
          isOpen={showInviteCodeModal}
          onClose={handleCloseInviteCodeModal}
          onProceed={handleInviteCodeProceed}
          selectedRole="guest"
          error={errorMessage}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default PrivateEventModal;